import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { paymentRepository } from '../repositories/payment.repository';
import { paymentKeys } from '../services/payments.keys';
import type { PaymentStatus, PaymentMode } from '../types/payment.types';

export const useCampaignPayments = (campaignId: string) => {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: paymentKeys.campaignPayments(campaignId),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }
      return paymentRepository.fetchCampaignPayments(campaignId);
    },
    enabled: !!campaignId && !!currentOrganization?.id,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useCampaignPaymentsSummary = useCampaignPayments;

export const useCreatePayment = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (paymentData: {
      campaign_influencer_id: string;
      amount: number;
      status: PaymentStatus;
      payment_mode?: PaymentMode;
      due_date?: string;
      notes?: string;
    }) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }
      return paymentRepository.addPayment(currentOrganization.id, paymentData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-payments'] });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      toast({
        title: 'Success',
        description: 'Payment record created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: 'Failed to create payment record',
        variant: 'destructive',
      });
      console.error('Error creating payment:', error);
    },
  });
};

export const useUpdatePaymentStatus = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      paymentId,
      status,
    }: {
      paymentId: string;
      status: PaymentStatus;
    }) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }
      return paymentRepository.updatePaymentStatus(paymentId, status);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-payments'] });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard-payments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-budget-calculations'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
      toast({
        title: 'Success',
        description: 'Payment status updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: 'Failed to update payment status',
        variant: 'destructive',
      });
      console.error('Error updating payment status:', error);
    },
  });
};

export const useCampaignPaymentMutations = () => {
  const createPayment = useCreatePayment();
  const updatePaymentStatus = useUpdatePaymentStatus();

  return {
    createPayment,
    updatePaymentStatus,
  };
};

